import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Grid,
  List,
  Stack,
  TextField,
  Typography,
  Button,
  Alert,
  Snackbar
} from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import pluginServices from 'app/services/plugin-services';
import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import JumboScrollbar from '@jumbo/components/JumboScrollbar';
import companyServices from 'app/services/company-services';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import PluginListItem from './PluginListItem';
import { LoadingButton } from '@mui/lab';

const VaryingField = (props) => {
  if (props.field.type === 'string' || props.field.type === 'password') {
    return (
      <TextField
        fullWidth
        type={props.field.type === 'password' ? 'password' : 'text'}
        required={props.field.required}
        label={props.field.label}
        value={props.value || ''}
        onChange={(e) => props.onChange(e)}
        helperText={props.field.help || ''}
        variant="outlined"
        sx={{ mt: 2 }}
      />
    )
  }

  if (props.field.type === 'button') {
    return (
      <Button
        variant="contained"
        sx={{
          mt: 2,
          px: 3,
          backgroundColor: "#ffe600",
          color: "#333",
          fontWeight: "bold",
          fontSize: "14px",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
          "&:hover": { backgroundColor: "#ffd600" }
        }}
        startIcon={
          <img
            src="https://http2.mlstatic.com/frontend-assets/ui-navigation/5.21.11/mercadolibre/logo__large_plus.png"
            alt="Mercado Libre"
            style={{ width: 84, height: 24 }}
          />
        }
        onClick={() => {
          const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://dev-api.haulers.cl";
          const origin = encodeURIComponent(window.location.href);
          window.location.href = `${API_BASE}/api/v1/auth/mercadolibre?origin=${origin}`;
        }}
      >
        {props.field.label}
      </Button>
    )
  }
}

const PluginsIndex = () => {
  const { authUser } = useJumboAuth();

  const [pluginsList, setPluginsList] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [listLoading, setListLoading] = useState(false)
  const [currentPlugin, setCurrentPlugin] = useState(null)
  const [currentCompanyPlugin, setCurrentCompanyPlugin] = useState(null)

  const [companyPlugins, setCompanyPlugins] = useState([])
  const [companyPluginsLoading, setCompanyPluginsLoading] = useState(false)

  const [pluginInToggleInstallation, setPluginInToggleInstallation] = useState(null)
  const [pluginInToggleActivation, setPluginInToggleActivation] = useState(null)

  const [pluginConfig, setPluginConfig] = useState({})
  const [saving, setSaving] = useState(false)

  // estado para mostrar mensajes de conexión Mercado Libre
  const [meliSnack, setMeliSnack] = useState({ open: false, severity: "info", text: "" });

  useEffect(() => {
    if (listLoading) {
      loadPlugins()
    }
    if (companyPluginsLoading) {
      loadCompanyPlugins()
    }
  }, [listLoading, companyPluginsLoading])

  useEffect(() => {
    setCompanyPluginsLoading(true)
    setListLoading(true)
  }, [])

  useEffect(() => {
    var newPC = currentCompanyPlugin?.data_values
    _.each(newPC, (v, k) => {
      if (currentCompanyPlugin.plugin.data_fields[k].type == 'password') {
        newPC[k] = ''
      }
    })
    setPluginConfig(newPC)
    setCurrentPlugin(currentCompanyPlugin?.plugin)
  }, [currentCompanyPlugin])

  // chequea query params para mostrar mensajes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const meli = params.get("meli");
    const reason = params.get("reason");

    if (meli) {
      if (meli === "connected") {
        setMeliSnack({
          open: true,
          severity: "success",
          text: "¡Mercado Libre conectado correctamente!",
        });
        setCompanyPluginsLoading(true);
        setListLoading(true);
      } else if (meli === "error") {
        setMeliSnack({
          open: true,
          severity: "error",
          text: `No se pudo conectar con Mercado Libre${reason ? `: ${decodeURIComponent(reason)}` : ""}`,
        });
      }

      params.delete("meli");
      params.delete("reason");
      const newQs = params.toString();
      const newUrl = `${window.location.pathname}${newQs ? `?${newQs}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const buildParams = () => {
    let par = []
    let pos = 0
    par.push(encodeURI(`q[m]=and`))
    if (searchQuery && searchQuery !== 0) {
      par.push(encodeURI(`q[g][${pos}][data_cont]=${searchQuery}`))
      pos += 1
    }
    par.push(`page=${currentPage}`)
    par = _.join(par, '&')
    return `?${par}`;
  }

  const handleSearchQueryChange = (newVal) => {
    setCurrentPage(1)
    setSearchQuery(newVal)
    setListLoading(true)
  }

  const debounceSearchQueryChange = _.debounce(handleSearchQueryChange, 450)

  const loadPlugins = () => {
    pluginServices.getPlugins(buildParams())
      .then(data => {
        setPluginsList(data.data)
        setTotalPages(data.meta.total_pages)
      })
      .catch(() => { })
      .finally(() => {
        setListLoading(false)
      });
  }

  const loadCompanyPlugins = () => {
    if (authUser) {
      companyServices.getCompanyPlugins(authUser?.current_company?.id)
        .then(data => {
          setCompanyPlugins(data)
        })
        .catch(() => { })
        .finally(() => {
          setCompanyPluginsLoading(false)
        });
    }
  }

  const renderInstalledPluginsList = (company_plugins, _loading = false) => {
    if (company_plugins) {
      return (
        <List disablePadding>
          {_.map(company_plugins, (cp, i) => {
            return (
              <PluginListItem
                plugin={cp.plugin}
                enabled={cp.enabled}
                inLoading={pluginInToggleActivation}
                enableConfigButton={cp.enabled}
                onText="Activado"
                offText="No activado"
                onToggle={(plug) => { togglePluginActivation(plug) }}
                onConfig={(plug) => { setCurrentCompanyPlugin(cp) }} />
            )
          })}
        </List>
      )
    } else if (!_loading) {
      return (
        <Typography textAlign="center">No existen datos</Typography>
      )
    }
  }

  const renderPluginsList = (plugins, _loading = false) => {
    if (plugins) {
      return (
        <List disablePadding>
          {_.map(plugins, (p, i) => {
            return (
              <PluginListItem
                plugin={p}
                enabled={_.find(companyPlugins, (cp) => { return cp.plugin.id === p.id }) ? true : false}
                inLoading={pluginInToggleInstallation}
                onText="Instalado"
                offText="No instalado"
                onToggle={(plug) => { togglePluginInstallation(plug) }} />
            )
          })}
        </List>
      )
    } else if (!_loading) {
      return (
        <Typography textAlign="center">No existen datos</Typography>
      )
    }
  }

  const togglePluginInstallation = (plugin) => {
    setPluginInToggleInstallation(plugin.id)
    setCurrentCompanyPlugin(null)
    pluginServices.togglePluginInstallation(plugin.id)
      .then(() => {
        setListLoading(true)
        setCompanyPluginsLoading(true)
      })
      .catch(() => { })
      .finally(() => {
        setPluginInToggleInstallation(null)
      });
  }

  const togglePluginActivation = (plugin) => {
    setPluginInToggleActivation(plugin.id)
    setCurrentCompanyPlugin(null)
    pluginServices.togglePluginActivation(plugin.id)
      .then(() => {
        setListLoading(true)
        setCompanyPluginsLoading(true)
      })
      .catch(() => { })
      .finally(() => {
        setPluginInToggleActivation(null)
      });
  }

  const savePluginConfig = () => {
    if (currentPlugin) {
      setSaving(true)
      pluginServices.saveConfig(currentPlugin.id, { data_values: pluginConfig })
        .then(() => { })
        .catch(() => { })
        .finally(() => {
          setSaving(false)
        });
    }
  }

  const handleFieldChange = (key, field, value) => {
    if (key) {
      var newPluginConfig = { ...pluginConfig }
      newPluginConfig[key] = value
      setPluginConfig(newPluginConfig)
    }
  }

  const renderPluginConfig = (plugin) => {
    if (plugin) {
      if (_.keys(plugin.data_fields).length > 0) {
        return (
          <React.Fragment>
            {_.map(plugin.data_fields, (field, key) => {
              if (field.show_in_form) {
                return (
                  <VaryingField field={field} value={pluginConfig[key]} help={field.help} onChange={(e) => { handleFieldChange(key, field, e.target.value) }} />
                )
              }
            })}
            <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }}>
              <LoadingButton
                loading={saving}
                variant="contained"
                color="primary"
                size='small'
                sx={{ minWidth: 140 }}
                loadingPosition="end"
                disabled={saving}
                onClick={() => savePluginConfig()}>
                {saving ? "Guardando" : "Guardar"}
              </LoadingButton>
            </Stack>
          </React.Fragment>
        )
      } else {
        return (
          <Typography sx={{ mt: 2 }}>Este plugin no posee parámetros para configurar</Typography>
        )
      }
    }
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <JumboCardQuick
          title="Instalados"
          headerSx={{
            pb: 1,
            '& .MuiCardHeader-action': {
              my: -.75
            }
          }}
          sx={{ mb: 2 }}>
          <JumboScrollbar
            autoHeight
            autoHeightMin={300}
            style={{ paddingRight: 16 }}>
            <Div sx={{ display: companyPluginsLoading ? 'block' : 'none', textAlign: 'center', my: 5 }}>
              <CircularProgress color="warning" />
            </Div>
            {renderInstalledPluginsList(companyPlugins, companyPluginsLoading)}
          </JumboScrollbar>
        </JumboCardQuick>

        <JumboCardQuick
          title="Disponibles"
          headerSx={{
            pb: 1,
            '& .MuiCardHeader-action': {
              my: -.75
            }
          }}
          sx={{ mb: 2 }}>
          <JumboScrollbar
            autoHeight
            autoHeightMin={300}
            style={{ paddingRight: 16 }}>
            <Div sx={{ display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5 }}>
              <CircularProgress color="warning" />
            </Div>
            {renderPluginsList(pluginsList, listLoading, "Desinstalar", "Instalar", pluginInToggleInstallation, togglePluginInstallation)}
          </JumboScrollbar>
        </JumboCardQuick>
      </Grid>
      <Grid item xs={12} md={6}>
        {currentPlugin &&
          <JumboCardQuick
            title="Configuración"
            headerSx={{
              pb: 1,
              '& .MuiCardHeader-action': {
                my: -.75
              }
            }}
            sx={{ mb: 2 }}>
            <Typography variant="h5">{currentPlugin.name}</Typography>
            {renderPluginConfig(currentPlugin)}
          </JumboCardQuick>
        }
      </Grid>

      <Snackbar
        open={meliSnack.open}
        autoHideDuration={5000}
        onClose={() => setMeliSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={meliSnack.severity}
          variant="filled"
          onClose={() => setMeliSnack(s => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {meliSnack.text}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default PluginsIndex;